import React, { useEffect, useState, useRef, Component } from 'react';
import { Leaf, Star, Heart, Sparkles, ArrowRight, CheckCircle2, PenTool, X, Play, Pause, Volume2 } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/tf6TyLNmTBPF9G9eK3FM9T/tapa_master_primer_ebook.jpg';
const MOCKUP_IMAGE = 'https://cdn.magicpatterns.com/uploads/sLFEZGJbufNCoyupgJVHMh/mockup_tapa_master2.png';
// Sample purchase data
const SAMPLE_PURCHASES = [{
  name: 'María G.',
  location: 'Madrid, España',
  time: 'hace 5 minutos'
}, {
  name: 'Carlos R.',
  location: 'Barcelona, España',
  time: 'hace 12 minutos'
}, {
  name: 'Ana M.',
  location: 'Valencia, España',
  time: 'hace 8 minutos'
}, {
  name: 'Pedro L.',
  location: 'Sevilla, España',
  time: 'hace 15 minutos'
}, {
  name: 'Laura S.',
  location: 'Bilbao, España',
  time: 'hace 20 minutos'
}, {
  name: 'Isabel F.',
  location: 'Málaga, España',
  time: 'hace 10 minutos'
}, {
  name: 'Roberto M.',
  location: 'Zaragoza, España',
  time: 'hace 18 minutos'
}];
// Purchase Notification Component
const PurchaseNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState(SAMPLE_PURCHASES[0]);
  useEffect(() => {
    const showNotification = () => {
      // Pick random purchase
      const randomPurchase = SAMPLE_PURCHASES[Math.floor(Math.random() * SAMPLE_PURCHASES.length)];
      setCurrentPurchase(randomPurchase);
      setIsVisible(true);
      // Hide after 5 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };
    // Show first notification after 3 seconds
    const initialTimeout = setTimeout(showNotification, 3000);
    // Show notification every 15 seconds
    const interval = setInterval(showNotification, 15000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
  if (!isVisible) return null;
  return <div className="fixed bottom-6 left-6 z-50 animate-slide-up" style={{
    animation: 'slideUp 0.5s ease-out'
  }} data-id="element-3">
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-[#D4735E]/20 p-4 max-w-sm backdrop-blur-sm flex gap-4 items-start relative overflow-hidden" data-id="element-4">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#FAF6F1] to-white opacity-90 -z-10" data-id="element-5"></div>

        {/* Product Image */}
        <div className="flex-shrink-0" data-id="element-6">
          <div className="w-16 h-16 rounded-lg overflow-hidden shadow-md border-2 border-[#D4735E]/30" data-id="element-7">
            <img src={PRODUCT_IMAGE} alt="Belleza Natural en Casa" className="w-full h-full object-cover" data-id="element-8" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-9">
          <div className="flex items-start justify-between gap-2 mb-1" data-id="element-10">
            <div className="flex items-center gap-2" data-id="element-11">
              <div className="w-2 h-2 bg-[#6B7456] rounded-full animate-pulse" data-id="element-12"></div>
              <span className="text-xs font-bold text-[#6B7456] uppercase tracking-wider" data-id="element-13">
                Compra Reciente
              </span>
            </div>
            <button onClick={() => setIsVisible(false)} className="text-[#5A5A5A] hover:text-[#2C2C2C] transition-colors" data-id="element-14">
              <X size={16} data-id="element-15" />
            </button>
          </div>

          <p className="font-bold text-[#2C2C2C] text-sm mb-1 font-handwriting" data-id="element-16">
            {currentPurchase.name} de {currentPurchase.location}
          </p>

          <div className="flex items-center gap-2 text-xs" data-id="element-17">
            <span className="text-[#5A5A5A] font-serif" data-id="element-18">
              Belleza Natural en Casa
            </span>
            <span className="text-[#D4735E] font-bold" data-id="element-19">• $9 USD</span>
          </div>

          <p className="text-xs text-[#5A5A5A] mt-1 italic" data-id="element-20">
            {currentPurchase.time}
          </p>
        </div>
      </div>
    </div>;
};
// Hand-drawn SVG Components
const Underline = () => <svg viewBox="0 0 200 20" className="w-full h-auto text-[#D4735E] opacity-80" preserveAspectRatio="none" data-id="element-21">
    <path d="M5,15 Q50,5 100,12 T195,10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-22" />
  </svg>;
const Circle = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 100 100" className={`absolute pointer-events-none ${className}`} preserveAspectRatio="none" data-id="element-23">
    <path d="M10,50 Q20,10 50,10 T90,50 T50,90 T10,50" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="5,5" data-id="element-24" />
  </svg>;
const ArrowDoodle = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 100 50" className={`absolute pointer-events-none ${className}`} preserveAspectRatio="none" data-id="element-25">
    <path d="M10,40 Q40,10 90,20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-26" />
    <path d="M80,15 L90,20 L85,30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-27" />
  </svg>;
const BrushStroke = ({
  className
}: {
  className?: string;
}) => <svg viewBox="0 0 300 30" className={`w-full h-auto ${className}`} preserveAspectRatio="none" data-id="element-28">
    <path d="M5,15 Q50,5 150,20 T295,10" fill="none" stroke="currentColor" strokeWidth="15" strokeLinecap="round" opacity="0.2" data-id="element-29" />
  </svg>;
const WaxSeal = () => <div className="relative w-24 h-24 flex items-center justify-center" data-id="element-30">
    <div className="relative text-[#FAF6F1] text-center transform -rotate-12" data-id="element-31"></div>
  </div>;
// Audio Player Component
const AudioPlayer = () => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', () => setIsPlaying(false));
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audio.currentTime = percentage * duration;
  };
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <div className="max-w-3xl mx-auto mb-32 relative" data-id="element-32">
      {/* Decorative elements */}
      <div className="absolute -top-8 -left-8 text-[#6B7456]/20 transform -rotate-12" data-id="element-33">
        <Volume2 size={48} strokeWidth={1.5} data-id="element-34" />
      </div>

      {/* Decorative arrow */}
      <ArrowDoodle className="w-16 h-16 text-[#D4735E]/30 absolute -bottom-8 -right-8 transform rotate-45" data-id="element-35" />
    </div>;
};
export function LandingPage() {
  return <div className="min-h-screen bg-[#FAF6F1] text-[#2C2C2C] font-serif selection:bg-[#E8967D] selection:text-white overflow-x-hidden" data-id="element-36">
      {/* Texture Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-40 z-0" style={{
      backgroundImage: 'url("https://www.transparenttextures.com/patterns/cream-paper.png")'
    }} data-id="element-37"></div>

      {/* Purchase Notification */}
      <PurchaseNotification data-id="element-38" />

      {/* Navigation / Header */}

      <main className="relative w-full max-w-6xl mx-auto py-16 md:py-24 z-10 bg-[#A1A1A139] pt-[0px] pb-[6px] space-y-[15px]" data-id="element-39">
        {/* Hero Section */}
        <section className="relative mb-32 md:mb-40" data-id="element-40">
          {/* Watercolor Background */}
          <div className="absolute -top-20 -left-20 w-[120%] h-[120%] bg-gradient-to-br from-[#E8967D]/20 via-[#F4C430]/10 to-transparent rounded-full blur-3xl opacity-60 animate-watercolor -z-10" data-id="element-41"></div>

          {/* Imagen FUERA del max-w-4xl */}
          <img src="https://i.ibb.co/7xnkF8gp/banner-landing-definitivo.png" alt="5 rituales que tu piel reconoce porque vienen de la naturaleza" className="w-full shadow-lg block mb-[80px] h-[250px] md:h-[700px] object-cover" data-id="element-42" />
          <div className="max-w-4xl mx-auto text-center relative space-y-[50px]" data-id="element-43">
            <div className="px-[40px]" data-id="element-44">
              <div className="w-[200px] h-[4px] bg-gradient-to-r from-[#6B7456] to-[#D4735E] mx-auto mb-[15px] animate-pulse" data-id="element-45"></div>
              <h1 className="md:text-6xl lg:text-7xl tracking-tight font-[500] font-['Open_Sans',_sans-serif] pt-[10px] pb-[10px] italic bg-gradient-to-r from-[#6B7456] to-[#D4735E] bg-clip-text text-transparent text-[20px] pl-[60px] pr-[60px] ml-[0px] mr-[0px]" data-id="element-46">
                "Sin complicaciones, sin ingredientes raros y sin gastar de más"
              </h1>
              <div className="w-[200px] h-[4px] bg-gradient-to-r from-[#D4735E] to-[#6B7456] mx-auto mt-[15px] animate-pulse ml-[55px] mr-[55px]" data-id="element-47"></div>
            </div>

            <div className="max-w-2xl mx-auto bg-white/60 p-8 rounded-lg backdrop-blur-sm border border-[#D4735E]/20 shadow-lg transform -rotate-1 pt-[20px] pb-[20px] pl-[20px] pr-[20px] space-y-[0px]" data-id="element-48">
              <div className="space-y-6 text-xl md:text-2xl leading-relaxed text-[#5A5A5A]" data-id="element-49">
                <p className="font-light italic text-[22px]" data-id="element-50">
                  Un tiempo en el que la belleza se preparaba en casa. En la
                  cocina, con ingredientes que reconocías.
                </p>
                <p className="font-handwriting text-[#D4735E] transform rotate-1 font-[600] text-[26px]" data-id="element-51">
                  Sin etiquetas interminables. Sin rutinas de 10 pasos. Sin esa
                  sensación de que "si no compro, no valgo".
                </p>
                <p className="pt-[0px] pb-[0px]" data-id="element-52"></p>
              </div>
            </div>
          </div>
        </section>

        {/* The Mirror Moment - Journal Style */}
        <section className="max-w-3xl mx-auto mb-32 relative" data-id="element-53">
          <div className="absolute -left-12 top-0 text-[#F4C430] transform -rotate-12 opacity-80" data-id="element-54">
            <Sparkles size={64} strokeWidth={1.5} data-id="element-55" />
          </div>

          <div className="bg-white p-8 md:p-12 shadow-xl transform rotate-1 relative paper-shadow pl-[32px] pr-[32px] pt-[10px] pb-[10px]" style={{
          borderRadius: '2px'
        }} data-id="element-56">
            {/* Tape Effect */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-32 h-8 tape" data-id="element-57"></div>

            <h2 className="text-3xl md:text-4xl text-[#D4735E] mb-8 font-handwriting font-[600]" data-id="element-58">
              Tal vez te ha pasado:
            </h2>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed font-serif relative z-10" data-id="element-59">
              <p data-id="element-60">Te miras al espejo y algo no encaja.</p>
              <div className="relative pl-6 border-l-4 border-[#6B7456]/40" data-id="element-61">
                <p data-id="element-62">
                  Miras tu estantería del baño llena de botes y piensas:
                  <span className="block mt-2 font-handwriting text-2xl text-[#6B7456] transform -rotate-1 font-[600]" data-id="element-63">
                    "¿Cómo puede ser que, con todo esto, mi piel siga igual de
                    apagada?"
                  </span>
                </p>
              </div>
              <p data-id="element-64">
                Te prometiste que este último producto sí iba a marcar la
                diferencia… y volvió a ser otra decepción silenciosa.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t-2 border-dashed border-[#D4735E]/30 text-center" data-id="element-65">
              <p className="text-xl md:text-2xl text-[#2C2C2C] font-handwriting font-[600]" data-id="element-66">
                Si te reconoces en esto, respira. No estás sola. Y no es culpa
                tuya.
              </p>
            </div>
          </div>
        </section>

        {/* The Problem - Collage Style */}
        <section className="max-w-5xl mx-auto mb-32" data-id="element-67">
          <h2 className="text-4xl md:text-5xl text-[#6B7456] font-medium text-center mb-16 relative inline-block left-1/2 transform -translate-x-1/2 pt-[20px] pb-[20px]" data-id="element-68">
            El baño lleno… y la piel apagada
            <div className="absolute -bottom-2 left-0 w-full h-3 bg-[#E8967D]/30 -z-10 transform -rotate-1 rounded-full" data-id="element-69"></div>
          </h2>

          <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-70">
            <div className="bg-[#FAF6F1] p-8 md:p-10 border-2 border-[#2C2C2C] hand-drawn-border transform -rotate-2 relative z-10 bg-white" data-id="element-71">
              <div className="space-y-4 text-lg leading-relaxed" data-id="element-72">
                <p className="font-bold text-[#6B7456] uppercase tracking-wider text-sm" data-id="element-73">
                  Imagina esta escena
                </p>
                <p data-id="element-74">
                  Es tarde. Por fin tienes un momento para ti. Entras al baño,
                  abres el armario y ahí están:
                </p>
                <ul className="list-none space-y-2 font-handwriting text-xl text-[#D4735E]" data-id="element-75">
                  <li className="text-[24px] font-[600]" data-id="element-76">
                    • Cremas de día y noche
                  </li>
                  <li className="text-[24px] font-[600]" data-id="element-77">
                    • Sérums y exfoliantes
                  </li>
                  <li className="text-[24px] font-[600]" data-id="element-78">
                    • Mascarillas y aceites
                  </li>
                  <li className="text-[24px] font-[600]" data-id="element-79">
                    • Tónicos interminables...
                  </li>
                </ul>
                <p className="italic text-[#5A5A5A] pt-4 border-t border-[#2C2C2C]/10" data-id="element-80">
                  Pero al mirarte al espejo… la realidad es otra.
                </p>
              </div>
            </div>

            <div className="bg-[#E8967D]/10 p-8 md:p-10 transform rotate-2 relative z-0 paper-shadow" data-id="element-81">
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#F4C430]/20 rounded-full blur-xl" data-id="element-82"></div>
              <div className="space-y-6 text-lg md:text-xl leading-relaxed" data-id="element-83">
                <p data-id="element-84">
                  La piel se ve cansada. El cabello ha perdido vida. Y tú te
                  sientes un poco estafada.
                </p>
                <p data-id="element-85">
                  No por un producto en concreto, sino por la idea entera de que
                  cuidarte tiene que ser tan complicado, tan caro y tan confuso.
                </p>
                <div className="relative" data-id="element-86">
                  <ArrowDoodle className="w-12 h-12 text-[#6B7456] absolute -left-8 top-0 transform -rotate-45" data-id="element-87" />
                  <p className="font-handwriting text-2xl text-[#D4735E] pl-6 font-[600]" data-id="element-88">
                    "¿De verdad esto es necesario para estar bien con mi piel?"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Brush Stroke Divider */}
        <div className="flex justify-center my-20 opacity-60 text-[#D4735E]" data-id="element-89">
          <BrushStroke className="w-64" data-id="element-90" />
        </div>

        {/* The Real Problem - Notebook Style */}
        <section className="max-w-4xl mx-auto mb-32 relative" data-id="element-91">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#6B7456] text-[#FAF6F1] px-6 py-2 rounded-sm shadow-md z-20 font-bold tracking-widest uppercase text-sm rotate-1" data-id="element-92">
            La Verdad
          </div>

          <div className="bg-white border border-[#D4735E]/20 p-8 md:p-16 shadow-2xl relative" style={{
          backgroundImage: 'linear-gradient(#E5E5E5 1px, transparent 1px)',
          backgroundSize: '100% 2rem'
        }} data-id="element-93">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-[#D4735E]/30" data-id="element-94"></div>

            <h2 className="text-3xl md:text-4xl text-[#2C2C2C] font-medium mb-8 pl-8 relative z-10" data-id="element-95">
              El problema real no es tu piel
            </h2>

            <div className="space-y-8 text-lg md:text-xl leading-[2rem] pl-8 relative z-10 font-handwriting text-[#5A5A5A]" data-id="element-96">
              <p className="font-serif text-[#2C2C2C] text-[20px] font-[600]" data-id="element-97">
                Nos han repetido una y otra vez:
              </p>
              <ul className="space-y-2 pl-4 border-l-2 border-[#6B7456]/30 ml-2" data-id="element-98">
                <li className="text-[22px] font-[600]" data-id="element-99">"Es la edad."</li>
                <li className="text-[22px] font-[600]" data-id="element-100">
                  "Es tu tipo de piel."
                </li>
                <li className="text-[22px] font-[600]" data-id="element-101">
                  "Es que tienes que probar esta nueva rutina."
                </li>
              </ul>

              <p className="font-bold text-[#D4735E] transform -rotate-1 bg-[#FAF6F1]/80 inline-block px-2 text-[25px]" data-id="element-102">
                Pero la verdad es otra.
              </p>

              <p className="font-serif text-[#2C2C2C] text-[20px]" data-id="element-103">
                El problema es que, durante años, te enseñaron a cuidarte desde
                la compra, no desde el conocimiento.
              </p>

              <div className="bg-[#FAF6F1] p-6 rounded-lg border-2 border-dashed border-[#6B7456]/30 transform rotate-1 mt-8" data-id="element-104">
                <p className="mb-4 font-bold text-[#6B7456] font-serif text-[21px]" data-id="element-105">
                  Te hicieron creer que:
                </p>
                <ul className="space-y-3 font-serif text-base" data-id="element-106">
                  <li className="flex items-start gap-3" data-id="element-107">
                    <span className="text-[#D4735E] font-bold" data-id="element-108">✕</span>
                    <span className="text-[20px] font-[600]" data-id="element-109">
                      Lo simple no era suficiente.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-110">
                    <span className="text-[#D4735E] font-bold" data-id="element-111">✕</span>
                    <span className="text-[20px] font-[600]" data-id="element-112">
                      Lo casero era "poco profesional".
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-113">
                    <span className="text-[#D4735E] font-bold" data-id="element-114">✕</span>
                    <span className="text-[20px] font-[600]" data-id="element-115">
                      Lo natural funcionaba "hasta cierto punto".
                    </span>
                  </li>
                </ul>
              </div>

              <p className="font-serif text-[#2C2C2C] mt-8 text-[20px]" data-id="element-116">
                Te alejaron de lo esencial. Te desconectaron de tu propia
                sabiduría.
              </p>
            </div>
          </div>
        </section>

        {/* Guilt Section */}
        <section className="max-w-3xl mx-auto mb-32 text-center" data-id="element-117">
          <h2 className="text-3xl md:text-4xl text-[#D4735E] font-medium mb-12 font-handwriting transform -rotate-1 pt-[20px] pb-[20px] pl-[20px] pr-[20px]" data-id="element-118">
            Cuando cuidarte se convierte en una fuente de culpa
          </h2>

          <div className="grid gap-6 text-left" data-id="element-119">
            <div className="bg-white p-6 shadow-md transform rotate-1 border-t-4 border-[#6B7456]" data-id="element-120">
              <p className="text-[20px]" data-id="element-121">
                Ves rutinas en redes sociales con 8, 10 o 12 pasos y piensas:{' '}
                <span className="font-handwriting text-[#6B7456] text-[22px]" data-id="element-122">
                  "Yo no tengo tiempo para eso."
                </span>
              </p>
            </div>
            <div className="bg-white p-6 shadow-md transform -rotate-1 border-t-4 border-[#E8967D]" data-id="element-123">
              <p className="text-[20px]" data-id="element-124">
                Te prometes empezar una rutina perfecta el lunes… y el jueves ya
                estás exhausta.
              </p>
            </div>
            <div className="bg-white p-6 shadow-md transform rotate-1 border-t-4 border-[#F4C430]" data-id="element-125">
              <p className="text-[20px]" data-id="element-126">
                Y, en el fondo, hay una idea que duele:{' '}
                <span className="font-handwriting text-xl text-[#D4735E]" data-id="element-127">
                  "Si no llego a todo, no me estoy cuidando como debería."
                </span>
              </p>
            </div>
          </div>

          <div className="mt-16 relative inline-block" data-id="element-128">
            <div className="absolute inset-0 bg-[#F4C430]/20 transform rotate-2 rounded-lg -z-10" data-id="element-129"></div>
            <div className="bg-white border-2 border-[#2C2C2C] p-8 hand-drawn-border" data-id="element-130">
              <p className="text-2xl md:text-3xl font-handwriting text-[#6B7456] leading-relaxed" data-id="element-131">
                Pero aquí viene una verdad que nadie te dijo con claridad:
                <br data-id="element-132" />
                <span className="font-bold text-[#D4735E]" data-id="element-133">
                  Cuidarte no debería complicarte la vida. Debería devolvértela.
                </span>
              </p>
            </div>
          </div>
        </section>

        {/* Philosophy Shift - Scrapbook Style */}
        <section className="max-w-5xl mx-auto mb-32" data-id="element-134">
          <div className="relative p-8 md:p-16" data-id="element-135">
            {/* Background Wash */}
            <div className="absolute inset-0 bg-[#FAF6F1] border-2 border-[#6B7456]/20 rounded-3xl transform rotate-1 shadow-xl" data-id="element-136"></div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8967D]/10 to-transparent rounded-3xl transform -rotate-1" data-id="element-137"></div>

            <div className="relative z-10" data-id="element-138">
              <h2 className="text-3xl md:text-5xl text-[#6B7456] font-medium mb-16 text-center font-handwriting" data-id="element-139">
                La belleza natural no se crea. Se restaura.
              </h2>

              <div className="grid md:grid-cols-3 gap-8" data-id="element-140">
                <div className="bg-white p-6 rounded-lg shadow-md transform -rotate-2 hover:rotate-0 transition-transform duration-300 border border-[#D4735E]/10" data-id="element-141">
                  <div className="w-12 h-12 bg-[#FAF6F1] rounded-full flex items-center justify-center mb-4 mx-auto border-2 border-[#D4735E] border-dashed" data-id="element-142">
                    <Leaf className="text-[#D4735E]" size={24} data-id="element-143" />
                  </div>
                  <h3 className="font-bold mb-3 text-[#2C2C2C] text-center font-handwriting text-[22px]" data-id="element-144">
                    Como volver a cocinar en casa
                  </h3>
                  <p className="text-[#5A5A5A] text-center text-[20px]" data-id="element-145">
                    Después de años de ultraprocesados. Tu cuerpo recuerda lo
                    natural. Lo reconoce. Responde distinto.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md transform rotate-2 hover:rotate-0 transition-transform duration-300 border border-[#D4735E]/10 mt-8 md:mt-0" data-id="element-146">
                  <div className="w-12 h-12 bg-[#FAF6F1] rounded-full flex items-center justify-center mb-4 mx-auto border-2 border-[#D4735E] border-dashed" data-id="element-147">
                    <Sparkles className="text-[#D4735E]" size={24} data-id="element-148" />
                  </div>
                  <h3 className="font-bold mb-3 text-[#2C2C2C] text-center font-handwriting text-[22px]" data-id="element-149">
                    Como limpiar una ventana empañada
                  </h3>
                  <p className="text-[#5A5A5A] text-center text-[20px]" data-id="element-150">
                    No necesitas añadir más cosas. Solo retiras lo que sobra y,
                    de repente, ves con claridad.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md transform -rotate-1 hover:rotate-0 transition-transform duration-300 border border-[#D4735E]/10" data-id="element-151">
                  <div className="w-12 h-12 bg-[#FAF6F1] rounded-full flex items-center justify-center mb-4 mx-auto border-2 border-[#D4735E] border-dashed" data-id="element-152">
                    <Heart className="text-[#D4735E]" size={24} data-id="element-153" />
                  </div>
                  <h3 className="font-bold mb-3 text-[#2C2C2C] text-center font-handwriting text-[22px]" data-id="element-154">
                    Como heredar un cuaderno de recetas
                  </h3>
                  <p className="text-[#5A5A5A] text-center text-[20px]" data-id="element-155">
                    Nada es estrafalario. Nada es excesivo. Son cosas simples…
                    que siguen funcionando hoy.
                  </p>
                </div>
              </div>

              <div className="mt-16 text-center" data-id="element-156">
                <p className="md:text-2xl font-medium text-[#6B7456] font-handwriting bg-white/80 inline-block px-6 py-3 rounded-full shadow-sm border border-[#6B7456]/20 transform rotate-1 text-[24px]" data-id="element-157">
                  No necesitas más capas. Necesitas menos ruido.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Failed Solutions - Torn Paper List */}
        <section className="max-w-3xl mx-auto mb-32" data-id="element-158">
          <h2 className="text-3xl md:text-4xl text-[#2C2C2C] font-medium mb-12 text-center" data-id="element-159">
            La verdad sobre las soluciones que ya probaste
          </h2>

          <div className="space-y-6" data-id="element-160">
            {[{
            title: 'Cosmética comercial',
            desc: 'Diseñada para que vuelvas a comprar, no para que entiendas tu piel.',
            color: 'border-[#D4735E]'
          }, {
            title: "Productos 'naturales' industrializados",
            desc: 'Etiquetas verdes, pero sigues sin saber qué estás poniendo en tu piel.',
            color: 'border-[#6B7456]'
          }, {
            title: 'Rutinas interminables',
            desc: 'Terminan generando culpa cuando no puedes seguir el ritmo de 10 pasos.',
            color: 'border-[#F4C430]'
          }].map((item, i) => <div key={i} className={`bg-white p-6 border-l-8 ${item.color} shadow-sm transform ${i % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0 transition-transform`} data-id="element-161">
                <h3 className="font-bold mb-2 font-serif text-[20px]" data-id="element-162">
                  {item.title}
                </h3>
                <p className="text-[#5A5A5A] font-handwriting text-[22px] font-[500]" data-id="element-163">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-164">
            <p className="text-[#D4735E] font-handwriting transform -rotate-1 text-[24px] font-[600] pt-[20px] pb-[20px]" data-id="element-165">
              No es que tú hayas fallado. El sistema está diseñado así.
            </p>
          </div>
        </section>

        {/* Solution / Product Intro - Golden Hour Feel */}
        <section className="max-w-5xl mx-auto mb-32 relative" data-id="element-166">
          <div className="absolute inset-0 bg-gradient-to-r from-[#F4C430]/10 to-[#E8967D]/10 rounded-3xl blur-3xl -z-10" data-id="element-167"></div>

          <div className="text-center mb-16" data-id="element-168">
            <span className="inline-block px-4 py-1 bg-[#D4735E] text-[#FAF6F1] font-bold tracking-widest uppercase transform -rotate-2 shadow-md mb-6 text-[18px]" data-id="element-169">
              La pieza que faltaba
            </span>
            <h2 className="text-4xl md:text-6xl text-[#6B7456] font-handwriting mb-6 font-[600]" data-id="element-170">
              Rituales Caseros Conscientes
            </h2>
            <p className="text-[#5A5A5A] max-w-2xl mx-auto font-serif text-[22px] ml-[14px] mr-[14px]" data-id="element-171">
              El problema no es que te falte fuerza de voluntad. Es que te
              faltaba un enfoque distinto.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-172">
            <div className="order-2 md:order-1 space-y-8 bg-white/80 p-8 rounded-2xl border border-[#D4735E]/20 shadow-lg backdrop-blur-sm" data-id="element-173">
              <h3 className="text-2xl font-bold text-[#2C2C2C] font-handwriting" data-id="element-174">
                ¿Qué significa esto?
              </h3>
              <ul className="space-y-4" data-id="element-175">
                <li className="flex gap-4 items-start" data-id="element-176">
                  <CheckCircle2 className="text-[#6B7456] shrink-0 mt-1" size={20} data-id="element-177" />
                  <span className="font-handwriting text-xl text-[#5A5A5A]" data-id="element-178">
                    No son recetas sueltas que olvidas al día siguiente.
                  </span>
                </li>
                <li className="flex gap-4 items-start" data-id="element-179">
                  <CheckCircle2 className="text-[#6B7456] shrink-0 mt-1" size={20} data-id="element-180" />
                  <span className="font-handwriting text-xl text-[#5A5A5A]" data-id="element-181">
                    No son truquitos virales vacíos.
                  </span>
                </li>
                <li className="flex gap-4 items-start" data-id="element-182">
                  <CheckCircle2 className="text-[#6B7456] shrink-0 mt-1" size={20} data-id="element-183" />
                  <span className="font-handwriting text-xl text-[#5A5A5A]" data-id="element-184">
                    Son prácticas que conectan cuerpo y hábitos.
                  </span>
                </li>
              </ul>
              <div className="pt-4 border-t border-dashed border-[#D4735E]/30" data-id="element-185">
                <p className="font-bold text-[#D4735E] italic transform rotate-1 text-[24px] pl-[20px] pr-[20px]" data-id="element-186">
                  "Sé qué hago, por qué lo hago y cómo me hace sentir."
                </p>
              </div>
            </div>

            <div className="order-1 md:order-2 flex justify-center relative" data-id="element-187">
              <div className="relative transform rotate-3 hover:rotate-0 transition-all duration-500" data-id="element-188">
                {/* Photo Frame Effect */}
                <div className="bg-white p-4 pb-12 shadow-2xl border border-gray-200" data-id="element-189">
                  <img src={PRODUCT_IMAGE} alt="Belleza Natural en Casa Ebook Cover" className="w-full max-w-xs" data-id="element-190" />
                  <div className="absolute bottom-4 right-4 font-handwriting text-[#5A5A5A] opacity-60 text-sm" data-id="element-191">
                    Volver a lo natural...
                  </div>
                </div>
                {/* Tape on corners */}
                <div className="absolute -top-3 -left-3 w-20 h-6 tape transform -rotate-45" data-id="element-192"></div>
                <div className="absolute -bottom-3 -right-3 w-20 h-6 tape transform -rotate-45" data-id="element-193"></div>

                {/* Wax Seal Badge */}
                <div className="absolute -top-6 -right-6 transform rotate-12 drop-shadow-lg" data-id="element-194">
                  <WaxSeal data-id="element-195" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Product Reveal - Artisan Card */}
        <section className="max-w-4xl mx-auto mb-32" data-id="element-196">
          <div className="bg-[#FAF6F1] border-2 border-[#2C2C2C] p-8 md:p-12 hand-drawn-border relative bg-white" data-id="element-197">
            <div className="text-center space-y-6" data-id="element-198">
              <h2 className="text-3xl md:text-5xl text-[#2C2C2C] font-handwriting font-[600]" data-id="element-199">
                Te presento: Belleza Natural en Casa
              </h2>
              <p className="text-[#6B7456] font-serif italic font-[600] text-[22px]" data-id="element-200">
                5 Rituales Caseros que Transforman tu Piel, tu Cabello y tu
                Rutina
              </p>

              <div className="bg-[#F4C430]/10 p-6 rounded-lg transform -rotate-1 my-8 border border-[#F4C430]/30" data-id="element-201">
                <p className="font-handwriting text-[#5A5A5A] text-[22px] font-[500]" data-id="element-202">
                  Este ebook nace de ese momento de crisis. En lugar de seguir
                  añadiendo cosas, hizo algo diferente:{' '}
                  <span className="text-[#D4735E] font-bold" data-id="element-203">
                    Recordó las recetas de antes.
                  </span>
                </p>
              </div>

              <button onClick={() => {
              document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }} className="group relative inline-block cursor-pointer" data-id="element-204">
                <div className="absolute inset-0 bg-[#2C2C2C] rounded-full transform translate-y-1 translate-x-1" data-id="element-205"></div>
                <div className="relative bg-[#D4735E] hover:bg-[#C86850] text-[#FAF6F1] font-bold px-10 py-4 rounded-full border-2 border-[#2C2C2C] transition-transform transform active:translate-y-1 active:translate-x-1 flex items-center gap-3 text-[24px]" data-id="element-206">
                  Accede Ahora al Ebook
                  <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform" data-id="element-207" />
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* What's Inside - Grid Collage */}
        <section className="max-w-5xl mx-auto mb-32" data-id="element-208">
          <div className="relative" data-id="element-209">
            <div className="absolute inset-0 bg-[#6B7456] transform rotate-1 rounded-3xl shadow-xl -z-10" data-id="element-210"></div>
            <div className="bg-[#FAF6F1] rounded-3xl p-8 md:p-16 border-2 border-[#6B7456]/20 relative overflow-hidden" data-id="element-211">
              {/* Decorative Blobs */}
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#E8967D]/20 rounded-full blur-3xl" data-id="element-212"></div>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F4C430]/20 rounded-full blur-3xl" data-id="element-213"></div>

              <h2 className="text-3xl md:text-4xl mb-12 text-center relative z-10 font-handwriting text-[#6B7456] font-[600]" data-id="element-214">
                ¿Qué vas a encontrar dentro?
              </h2>

              <div className="grid md:grid-cols-2 gap-12 relative z-10" data-id="element-215">
                <div className="space-y-6" data-id="element-216">
                  <p className="opacity-90 font-serif text-[22px]" data-id="element-217">
                    No son 100 rituales imposibles. Son 5 rituales claros,
                    replicables y adaptables.
                  </p>
                  <ul className="space-y-4" data-id="element-218">
                    {['Fácil de entender, sin conocimientos previos.', 'Ingredientes simples y cercanos.', 'Encaja en el ritmo real de tu vida.'].map((item, i) => <li key={i} className="flex items-start gap-3 transform hover:translate-x-1 transition-transform" data-id="element-219">
                        <div className="bg-[#D4735E]/20 p-1 rounded-full mt-1" data-id="element-220">
                          <CheckCircle2 size={16} className="text-[#D4735E]" data-id="element-221" />
                        </div>
                        <span className="font-handwriting text-[#2C2C2C] text-[22px] font-[500]" data-id="element-222">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white p-8 rounded-xl shadow-lg transform rotate-2 border border-[#6B7456]/10" data-id="element-223">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-16 h-4 bg-[#D4735E]/30 rounded-full blur-sm" data-id="element-224"></div>
                  <h3 className="text-2xl font-handwriting mb-6 text-[#D4735E] text-center font-[600]" data-id="element-225">
                    Resultados Reales
                  </h3>
                  <ul className="space-y-4 text-sm md:text-base font-serif" data-id="element-226">
                    <li className="flex gap-3 items-center" data-id="element-227">
                      <Leaf size={16} className="text-[#6B7456]" data-id="element-228" />
                      <span className="text-[20px]" data-id="element-229">
                        Piel más equilibrada y menos reactiva.
                      </span>
                    </li>
                    <li className="flex gap-3 items-center" data-id="element-230">
                      <Leaf size={16} className="text-[#6B7456]" data-id="element-231" />
                      <span className="text-[20px]" data-id="element-232">
                        Cabello más vivo desde la raíz.
                      </span>
                    </li>
                    <li className="flex gap-3 items-center" data-id="element-233">
                      <Leaf size={16} className="text-[#6B7456]" data-id="element-234" />
                      <span className="text-[20px]" data-id="element-235">
                        Rutinas simples y sostenibles.
                      </span>
                    </li>
                    <li className="flex gap-3 items-center" data-id="element-236">
                      <Leaf size={16} className="text-[#6B7456]" data-id="element-237" />
                      <span className="text-[20px]" data-id="element-238">
                        Mayor conexión contigo misma.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="max-w-5xl mx-auto mb-32" data-id="element-239">
          <h2 className="text-3xl md:text-4xl text-[#6B7456] font-medium text-center mb-16 font-handwriting pt-[20px] pb-[20px] pl-[20px] pr-[20px]" data-id="element-240">
            Lo que dicen quienes ya lo probaron
          </h2>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-241">
            {/* Testimonial 1 - Laura M. */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-[#D4735E]/20 transform -rotate-1 hover:rotate-0 transition-transform duration-300" data-id="element-242">
              <div className="flex items-center gap-4 mb-4" data-id="element-243">
                <img src="https://cdn.magicpatterns.com/uploads/iFSKcJNffUk35C75SdeXpX/1-removebg-preview.png" alt="Laura M." className="w-16 h-16 rounded-full object-cover border-2 border-[#D4735E]/30" data-id="element-244" />
                <div data-id="element-245">
                  <h3 className="font-bold text-[#2C2C2C] font-handwriting text-lg" data-id="element-246">
                    Laura M.
                  </h3>
                  <p className="text-sm text-[#5A5A5A]" data-id="element-247">Madrid</p>
                  <div className="text-[#F4C430]" data-id="element-248">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-[#5A5A5A] leading-relaxed font-serif text-sm" data-id="element-249">
                "Llevaba años gastando una fortuna en cremas y tratamientos que
                prometían milagros y no cumplían nada. Empecé con el exfoliante
                de café y azúcar casi sin creerlo… y desde la primera semana
                noté mi piel diferente. Más suave, más luminosa. Ahora no podría
                vivir sin mis rituales de los domingos."
              </p>
            </div>

            {/* Testimonial 2 - Cristina R. */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-[#D4735E]/20 transform rotate-1 hover:rotate-0 transition-transform duration-300" data-id="element-250">
              <div className="flex items-center gap-4 mb-4" data-id="element-251">
                <img src="https://cdn.magicpatterns.com/uploads/vyw5h3ndmrp8LAvY9SYifm/2-removebg-preview.png" alt="Cristina R." className="w-16 h-16 rounded-full object-cover border-2 border-[#D4735E]/30" data-id="element-252" />
                <div data-id="element-253">
                  <h3 className="font-bold text-[#2C2C2C] font-handwriting text-lg" data-id="element-254">
                    Cristina R.
                  </h3>
                  <p className="text-sm text-[#5A5A5A]" data-id="element-255">Barcelona</p>
                  <div className="text-[#F4C430]" data-id="element-256">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-[#5A5A5A] leading-relaxed font-serif text-sm" data-id="element-257">
                "Lo que más me sorprendió fue lo del cabello. Tenía las puntas
                destrozadas y había probado de todo. Con el aceite de almendra,
                en menos de un mes mi pelo dejó de romperse y empezó a crecer
                con una fuerza que no había visto en años. Y lo mejor: cuesta
                casi nada."
              </p>
            </div>

            {/* Testimonial 3 - Ana V. */}
            <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-[#D4735E]/20 transform -rotate-1 hover:rotate-0 transition-transform duration-300" data-id="element-258">
              <div className="flex items-center gap-4 mb-4" data-id="element-259">
                <img src="https://cdn.magicpatterns.com/uploads/byWb8fLbyvZQJPwhvzFun2/3-removebg-preview.png" alt="Ana V." className="w-16 h-16 rounded-full object-cover border-2 border-[#D4735E]/30" data-id="element-260" />
                <div data-id="element-261">
                  <h3 className="font-bold text-[#2C2C2C] font-handwriting text-lg" data-id="element-262">
                    Ana V.
                  </h3>
                  <p className="text-sm text-[#5A5A5A]" data-id="element-263">Sevilla</p>
                  <div className="text-[#F4C430]" data-id="element-264">⭐⭐⭐⭐⭐</div>
                </div>
              </div>
              <p className="text-[#5A5A5A] leading-relaxed font-serif text-sm" data-id="element-265">
                "Soy de las que pensaba que lo natural era complicado o que no
                funcionaba de verdad. Este ebook me demostró que estaba
                equivocada. La mascarilla de aguacate con miel se convirtió en
                mi ritual favorito. Mi piel está hidratada, sin irritaciones, y
                lo preparo en tres minutos con cosas que ya tenía en casa."
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Section - Ticket Style */}
        <section id="pricing" className="max-w-4xl mx-auto mb-32" data-id="element-266">
          <div className="relative bg-white rounded-lg shadow-2xl overflow-hidden border-2 border-[#D4735E] border-dashed p-2" data-id="element-267">
            <div className="bg-[#FAF6F1] border border-[#D4735E]/20 p-8 md:p-12 rounded-lg relative" data-id="element-268">
              {/* Cutout circles for ticket effect */}
              <div className="absolute top-1/2 -left-4 w-8 h-8 bg-[#FAF6F1] rounded-full border-r-2 border-[#D4735E] border-dashed transform -translate-y-1/2" data-id="element-269"></div>
              <div className="absolute top-1/2 -right-4 w-8 h-8 bg-[#FAF6F1] rounded-full border-l-2 border-[#D4735E] border-dashed transform -translate-y-1/2" data-id="element-270"></div>

              <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-271">
                <div className="relative flex justify-center" data-id="element-272">
                  <div className="absolute inset-0 bg-[#F4C430]/20 rounded-full blur-2xl" data-id="element-273"></div>
                  <img src={MOCKUP_IMAGE} alt="Belleza Natural en Casa - Mockup" className="w-full max-w-xs transform hover:scale-105 transition-transform duration-500 relative z-10 drop-shadow-xl" data-id="element-274" />
                  <div className="absolute -bottom-6 -right-6 z-20 transform rotate-12" data-id="element-275">
                    <div className="w-32 h-32 bg-[#D4735E] rounded-full flex flex-col text-[#FAF6F1] shadow-lg border-4 border-white border-dashed items-center justify-center" data-id="element-276">
                      <span className="font-bold uppercase text-[18px] pl-[0px] pr-[0px] pt-[0px] pb-[0px]" data-id="element-277">
                        Solo
                      </span>
                      <span className="font-bold font-handwriting text-[32px]" data-id="element-278">
                        $7
                      </span>
                      <span className="text-[18px] pt-[0px] pb-[0px] font-[500]" data-id="element-279">
                        USD
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-280">
                  <div data-id="element-281">
                    <h2 className="text-3xl text-[#2C2C2C] mb-2 font-handwriting pt-[0px] pb-[0px] pl-[0px] pr-[0px] font-[600]" data-id="element-282">
                      Belleza Natural en Casa
                    </h2>
                    <p className="uppercase tracking-wider font-bold text-[#6B7456] text-[18px] pt-[10px] pb-[10px]" data-id="element-283">
                      Formato Digital • Acceso Inmediato
                    </p>
                  </div>

                  <div className="space-y-3 font-handwriting text-xl text-[#5A5A5A]" data-id="element-284">
                    <div className="flex items-center gap-3" data-id="element-285">
                      <CheckCircle2 size={20} className="text-[#D4735E]" data-id="element-286" />
                      <span className="font-[500]" data-id="element-287">
                        5 rituales caseros completos
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-288">
                      <CheckCircle2 size={20} className="text-[#D4735E]" data-id="element-289" />
                      <span className="font-[500]" data-id="element-290">
                        Guía práctica paso a paso
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-291">
                      <CheckCircle2 size={20} className="text-[#D4735E]" data-id="element-292" />
                      <span className="font-[500]" data-id="element-293">Ingredientes simples</span>
                    </div>
                  </div>

                  {/* Bonos Section */}
                  <div className="border-t-2 border-dashed border-[#D4735E]/30 pt-6 mt-6" data-id="element-294">
                    <div className="flex items-center gap-2 mb-6" data-id="element-295">
                      <Sparkles size={20} className="text-[#F4C430]" data-id="element-296" />
                      <h3 className="text-2xl font-bold text-[#D4735E] font-handwriting" data-id="element-297">
                        + 2 Bonos Gratis Incluidos
                      </h3>
                      <Sparkles size={20} className="text-[#F4C430]" data-id="element-298" />
                    </div>

                    <div className="space-y-4" data-id="element-299">
                      {/* Bonus 1 */}
                      <div className="bg-gradient-to-br from-[#2C2C2C] to-[#3A3A3A] p-4 rounded-lg border-2 border-[#F4C430]/30 relative overflow-hidden pt-[30px] pb-[30px] pl-[16px] pr-[16px]" data-id="element-300">
                        <div className="absolute top-2 right-2 bg-[#F4C430] text-[#2C2C2C] text-xs font-bold px-3 py-1 rounded-full transform rotate-12 pt-[2px] pb-[2px]" data-id="element-301">
                          GRATIS
                        </div>
                        <div className="flex gap-4 items-start" data-id="element-302">
                          <img src="https://cdn.magicpatterns.com/uploads/jyidhr94vjdMp2Mta6AJ9J/mockup_bono_1_tapa.png" alt="Bonus 1 - El Mapa del Baño Despejado" className="w-32 h-32 rounded-lg shadow-md flex-shrink-0" data-id="element-303" />
                          <div className="flex-1" data-id="element-304">
                            <h4 className="text-[#F4C430] font-bold mb-1 text-[20px] pt-[0px] pb-[0px]" data-id="element-305">
                              BONUS 1 — El Mapa del Baño Despejado™
                            </h4>
                            <p className="text-[#FAF6F1] text-[16px]" data-id="element-306">
                              Guía práctica para limpiar tu baño de productos
                              sin culpa, en solo 20 minutos
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Bonus 2 */}
                      <div className="bg-gradient-to-br from-[#F5EFE7] to-[#E8DCC4] p-4 rounded-lg border-2 border-[#6B7456]/30 relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-307">
                        <div className="absolute top-2 right-2 bg-[#6B7456] text-[#FAF6F1] text-xs font-bold px-3 py-1 rounded-full transform rotate-12 pt-[4px] pb-[4px]" data-id="element-308">
                          GRATIS
                        </div>
                        <div className="flex gap-4 items-start" data-id="element-309">
                          <img src="https://cdn.magicpatterns.com/uploads/hT8E4aj79T9KBiRK4xDnhk/mockup_bono_2_tapa.png" alt="Bonus 2 - Recetario Exprés" className="w-32 h-32 rounded-lg shadow-md flex-shrink-0" data-id="element-310" />
                          <div className="flex-1" data-id="element-311">
                            <h4 className="text-[#6B7456] font-bold mb-1 text-[20px] pt-[0px] pb-[0px]" data-id="element-312">
                              BONUS 2 — Recetario Exprés de Belleza Casera
                            </h4>
                            <p className="text-[#5A5A5A] text-[16px]" data-id="element-313">
                              10 recetas con solo 3 ingredientes para
                              transformar tu piel hoy mismo
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-center" data-id="element-314">
                      <p className="text-[#D4735E] font-semibold italic text-[20px]" data-id="element-315">
                        Valor total de los bonos: $29.98 • Hoy: GRATIS
                      </p>
                    </div>
                  </div>

                  <button onClick={() => {
                  // Fire Meta Pixel AddToCart event
                  if (typeof window !== 'undefined' && (window as any).fbq) {
                    ;
                    (window as any).fbq('track', 'AddToCart');
                  }
                  window.open('https://debuenavida.com/cart/56663082369359:1', '_blank');
                }} className="w-full bg-[#D4735E] hover:bg-[#C86850] text-[#FAF6F1] font-bold px-8 py-4 rounded-sm shadow-md transition-all transform hover:-translate-y-1 flex justify-center items-center gap-2 border-2 border-[#2C2C2C] hand-drawn-border cursor-pointer text-[24px]" data-id="element-316">
                    Accede Ahora por $7 USD
                  </button>

                  <div className="text-center" data-id="element-317">
                    <p className="text-xs text-[#5A5A5A] font-serif italic" data-id="element-318">
                      🔒 Garantía total de 14 días. Si no estás completamente
                      satisfecha te devuelvo el 100% de tu dinero. Sin preguntas
                      ni complicaciones.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA - Handcrafted */}
        <section className="max-w-4xl mx-auto text-center relative pb-20" data-id="element-319">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-320">
            <WaxSeal data-id="element-321" />
          </div>

          <div className="bg-[#D4735E] text-[#FAF6F1] rounded-t-3xl p-12 md:p-20 shadow-2xl relative overflow-hidden" style={{
          borderRadius: '50% 50% 0 0 / 20% 20% 0 0'
        }} data-id="element-322">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mt-[30px] mb-[30px]" data-id="element-323"></div>

            <div className="relative z-10 space-y-8 max-w-2xl mx-auto pt-8" data-id="element-324">
              <h2 className="text-4xl md:text-5xl font-medium font-handwriting leading-tight" data-id="element-325">
                Da el primer paso hacia lo natural
              </h2>
              <p className="text-xl opacity-90 font-serif" data-id="element-326">
                Empieza hoy mismo a cuidarte como siempre debió ser:
              </p>
              <p className="text-3xl font-bold font-handwriting text-[#F4C430] transform -rotate-2 inline-block border-b-2 border-[#F4C430] border-dashed pb-1" data-id="element-327">
                Simple. Consciente. Real.
              </p>

              <div className="pt-8" data-id="element-328">
                <button onClick={() => {
                window.open('https://debuenavida.com/cart/56663082369359:1', '_blank');
              }} className="bg-[#FAF6F1] text-[#D4735E] hover:bg-white font-bold text-xl px-12 py-5 rounded-full shadow-xl transition-all transform hover:-translate-y-1 inline-flex items-center gap-3 border-4 border-[#D4735E]/30 cursor-pointer" data-id="element-329">
                  Haz Clic y Vuelve a Casa
                  <ArrowRight size={24} data-id="element-330" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <footer className="bg-[#2C2C2C] text-[#FAF6F1]/60 text-center py-12 text-sm font-serif" data-id="element-331">
          <p data-id="element-332">
            © {new Date().getFullYear()} Belleza Natural en Casa. Hecho con ❤️
            y consciencia.
          </p>
        </footer>
      </main>
    </div>;
}